import { Card } from "@/components/ui/Card"
import { cn } from "@/lib/utils"
import { formatMoneyShort } from "@/lib/money"
import { num } from "../data"

// One line of the agenda: a product on High DOC, a batch on Expiring soon.
export interface AgendaRow {
  id: string
  name: string
  subtitle: string[] // shown on one line under the name, joined with " · "
  qty: number
  value: number
  keyValue: string
}

interface Props {
  rows: AgendaRow[]
  selectedId: string
  actionCount: (id: string) => number
  keyLabel: string
  onSelect: (id: string) => void
}

// The agenda: every row in this part, one line each. The room's current row
// is tinted; the last column counts its actions.
export default function ProductList({ rows, selectedId, actionCount, keyLabel, onSelect }: Props) {
  return (
    <Card className="overflow-hidden">
      <div className="flex items-center gap-3 border-b bg-muted/60 px-4 py-2 text-xs font-semibold uppercase tracking-wide">
        <span className="flex-1">Product</span>
        <span className="w-14 text-right">Qty</span>
        <span className="w-16 text-right">Value</span>
        <span className="w-24 text-right">{keyLabel}</span>
        <span className="w-14 text-right">Actions</span>
      </div>
      {rows.map((row) => {
        const selected = row.id === selectedId
        const count = actionCount(row.id)
        return (
          <div
            key={row.id}
            role="button"
            onClick={() => onSelect(row.id)}
            className={cn(
              "flex cursor-pointer items-center gap-3 border-b px-4 py-3 last:border-b-0 hover:bg-muted/50",
              selected && "bg-primary/10 shadow-[inset_3px_0_0_var(--primary)] hover:bg-primary/10",
            )}
          >
            <div className="min-w-0 flex-1">
              <div className={cn("line-clamp-2 text-sm", selected ? "font-semibold" : "font-medium")}>{row.name}</div>
              {/* Each part stays on one line, so a date never splits. */}
              <div className="flex flex-wrap gap-x-1 text-xs text-muted-foreground">
                {row.subtitle.map((part, i) => (
                  <span key={i} className="whitespace-nowrap">
                    {part}
                    {i < row.subtitle.length - 1 && " ·"}
                  </span>
                ))}
              </div>
            </div>
            <span className="w-14 text-right text-sm tabular-nums">{num(row.qty)}</span>
            <span className="w-16 text-right text-sm tabular-nums">{formatMoneyShort(row.value, "VND")}</span>
            <span className="w-24 whitespace-nowrap text-right text-sm font-semibold tabular-nums">{row.keyValue}</span>
            <span className="w-14 text-right text-sm tabular-nums">
              {count > 0 ? (
                count
              ) : (
                <span className="text-muted-foreground">None</span>
              )}
            </span>
          </div>
        )
      })}
    </Card>
  )
}
